import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TipoDocumento } from 'src/app/models/TipoDocumento';
import { UsuarioService } from 'src/app/services/usuario.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-tipo-documento',
    templateUrl: './tipo-documento.component.html',
    styleUrls: ['./tipo-documento.component.css'],
    standalone: false
})
export class TipoDocumentoComponent {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);
  private aRoute = inject(ActivatedRoute);

  // estado
  error   = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  id?: number;
  operacion = 'Agregar ';
  botonOperacion = 'Agregar ';
  
  // forms
  moreTipoDocumento!: FormGroup;

  constructor(){
      this.createForm();
      this.id = Number(this.aRoute.snapshot.paramMap.get('id'));
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // Si necesitás (re)cargar usuario/cliente desde backend, hacelo bajo el loader:
    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        await this._authService.ensureSession();

        if(this.id != 0){
          // Es editar
          this.operacion = "Editar ";
          this.botonOperacion = "Actualizar ";
          const res = await firstValueFrom(this._usuarioService.getTipoDocumento(this.id!));

          if (!res) {
            // No existe => no tiene sentido editar
            this.error.set('El Tipo de Documento no existe o fue eliminada.');
            this.router.navigate(['/more-tipos-documentos']);
            return;
          }

          this.moreTipoDocumento.patchValue({
            descripcion: res?.x_descripcion ?? ''
          });
        }
      } catch (e: any) {
        console.error(e);
        this.error = e?.message || 'No se pudo cargar tu cuenta.';
        this.router.navigate(['/sign-in']);
      }
    });
  }

  createForm() {
    this.moreTipoDocumento = this._fb.group({
      descripcion: ['', Validators.required ]
    });
  }

  async saveTipoDocumento(){
    // Evita doble submit si ya está cargando
    if (this.loading()) return;
    
    this.error.set('');

    const v = this.moreTipoDocumento.getRawValue();

    if (!v.descripcion?.trim()) { this.error.set('Debe completar la Descripción'); return; }

    // Respeta validaciones de template/reactivas
    if (this.moreTipoDocumento.invalid) return;

    const isEdit = !!this.id;
    
    const tipoDocumento: TipoDocumento = {
      x_descripcion: v.descripcion.trim()
    }

    try {
      await this._loadingService.run(async () => {
        if (this.id !== 0) {
          // Editar
          await firstValueFrom(this._usuarioService.updateTipoDocumento(tipoDocumento));
        } else {
          // Crear
          await firstValueFrom(this._usuarioService.createTipoDocumento(tipoDocumento));
        }
      });

      await this.router.navigate(['/more-tipos-documentos']);
    } catch (e) {
      console.error(e);
      this.error.set(this.id !== 0
        ? 'Error al actualizar el Tipo de Documento'
        : 'Error al agregar el Tipo de Documento');
    }
  }

  back(){
    this._location.back();
  }
}
